/**
 * Item 238 — set the default events view (item 236) for every camera in a group.
 *
 * The server decides which cameras the user may change (the camera owner, staff or a
 * superuser), so every choice is first sent as a dry run and the dialog shows its counts;
 * Apply repeats the same call for real. A database-only preference: nothing reaches the
 * cameras, so there is no preview token as in bulk settings.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { toast } from '@/lib/toast'
import type { CameraGroup, DefaultEventCategory } from '@/types'

const VIEWS: DefaultEventCategory[] = ['animal', 'human', 'other', 'all']

interface Props {
  group: CameraGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GroupDefaultViewDialog({ group, open, onOpenChange }: Props) {
  const { t } = useTranslation('groups')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const qc = useQueryClient()
  const [value, setValue] = useState<DefaultEventCategory>('animal')

  const preview = useQuery({
    queryKey: queryKeys.cameraGroups.defaultViewPreview(group.id, value),
    queryFn: ({ signal }) => cameraGroupsApi.setDefaultEventsView(group.id, value, true, signal),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const apply = useMutation({
    mutationFn: () => cameraGroupsApi.setDefaultEventsView(group.id, value, false),
    onSuccess: (r) => {
      toast.success(r.skipped.length
        ? t('defaultView.appliedSkipped', { count: r.updated.length, skipped: r.skipped.length })
        : t('defaultView.applied', { count: r.updated.length }))
      qc.invalidateQueries({ queryKey: ['cameras'] })
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      // All Cameras (superusers) keys its rows and edit sheet outside ['cameras']; a stale
      // sheet sends default_event_category on save and would put the old tab back.
      qc.invalidateQueries({ queryKey: ['all-cameras'] })
      qc.invalidateQueries({ queryKey: ['camera'] })
      onOpenChange(false)
    },
    onError: () => toast.error(t('defaultView.failed')),
  })

  const counts = preview.data
  const canApply = !!counts && !preview.isFetching && counts.updated.length > 0 && !apply.isPending

  return (
    <Dialog.Root open={open} onOpenChange={apply.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">{t('defaultView.title', { name: group.name })}</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('defaultView.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={apply.isPending}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-4 space-y-1.5">
            <label className="text-sm font-medium" htmlFor="group-default-view">{t('defaultView.label')}</label>
            <Select value={value} onValueChange={(v) => setValue(v as DefaultEventCategory)} disabled={apply.isPending}>
              <SelectTrigger id="group-default-view">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIEWS.map((v) => (
                  <SelectItem key={v} value={v}>{te(`tabs.${v}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 min-h-[4.5rem] rounded-lg bg-muted/50 p-3 text-sm" aria-live="polite">
            {preview.isError ? (
              <p className="text-destructive">{t('defaultView.previewFailed')}</p>
            ) : !counts || preview.isFetching ? (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />{t('defaultView.checking')}
              </p>
            ) : (
              <ul className="space-y-0.5">
                <li className="font-medium">{t('defaultView.willChange', { count: counts.updated.length })}</li>
                {counts.unchanged.length > 0 && (
                  <li className="text-muted-foreground">{t('defaultView.alreadySet', { count: counts.unchanged.length })}</li>
                )}
                {counts.skipped.length > 0 && (
                  <li className="text-muted-foreground">{t('defaultView.notYours', { count: counts.skipped.length })}</li>
                )}
              </ul>
            )}
          </div>

          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={apply.isPending}>
              {tc('actions.cancel')}
            </Button>
            <Button className="flex-1" onClick={() => apply.mutate()} disabled={!canApply}>
              {apply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('defaultView.apply')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
